/**
 * Shared presentational helpers used across the three admin pages:
 *   - summary card (white panel, 8px radius, soft shadow, optional accent border)
 *   - stat tile (light-bg tile in modals)
 *   - loading row (centered, muted cell spanning the full table width)
 */
import type { ReactNode } from 'react';

export interface SummaryCardProps {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}

export function SummaryCard({ label, value, highlight = false }: SummaryCardProps): JSX.Element {
  return (
    <div
      className={[
        'flex items-center gap-4 rounded-lg bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.1)]',
        highlight ? 'border border-[color:var(--accent)]' : 'border border-[color:var(--gray-200)]',
      ].join(' ')}
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-[color:var(--text-dark)] opacity-70">
          {label}
        </div>
        <div className="mt-1 truncate text-2xl font-semibold text-[color:var(--text-dark)] font-[var(--font-heading)]">
          {value}
        </div>
      </div>
    </div>
  );
}

export interface StatProps {
  label: string;
  value: ReactNode;
  small?: boolean;
}

export function Stat({ label, value, small = false }: StatProps): JSX.Element {
  return (
    <div className="rounded-md bg-[var(--light-bg)] p-3 text-center">
      <div className="text-xs uppercase tracking-wide text-[color:var(--text-dark)] opacity-70">
        {label}
      </div>
      <div
        className={`mt-1 font-semibold text-[color:var(--text-dark)] font-[var(--font-heading)] ${
          small ? 'text-sm' : 'text-lg'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export interface LoaderRowProps {
  colSpan: number;
  message?: string;
  tone?: 'neutral' | 'error';
}

export function LoaderRow({
  colSpan,
  message = 'Loading...',
  tone = 'neutral',
}: LoaderRowProps): JSX.Element {
  const color = tone === 'error' ? 'text-red-600' : 'text-[color:var(--text-dark)]';
  return (
    <tr>
      <td colSpan={colSpan} className={`px-4 py-8 text-center text-sm ${color}`}>
        {message}
      </td>
    </tr>
  );
}
